import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Bracket, IRoundProps, Seed, SeedItem, SeedTeam, IRenderSeedProps } from 'react-brackets';
import ApiUrl from '../../utils/config';
import { useAuth } from '../Auth/AuthContext';
import AppContext from '../../AppContext';
import { handleError } from '../../utils';
import {
  BracketClient,
  BracketDetailsModel,
  BracketPreviewModel,
  CreateRepechageRequest,
  Match,
  MatchApproveDto,
  Participant,
  RepechageSide,
  RoundApproveDto,
  Tournament,
  TournamentClient,
  WinnerModel
} from '../../utils/apiClient';
import BracketsPreview from '../TournamentList/BracketsPreview';
import MatchConductModal from '../MatchConduct/MatchConductModal';
import { bracketPrintClient } from '../../utils/bracketPrintApi';
import { reorderMatches } from '../../utils/matchReorderApi';
import './BracketView.scss';

type MainTab = 'bracket' | 'matches';
type SubTab = 'main' | 'group-a' | 'group-b' | 'playoff' | 'repechage-left' | 'repechage-right';

const getStageLabel = (
  roundIndex: number,
  totalRounds: number,
  matchCount: number,
  isRepechage: boolean,
  roundNumber: number
) => {
  const roundsFromFinal = totalRounds - 1 - roundIndex;
  if (roundsFromFinal <= 0) return isRepechage ? 'Финал за бронзу' : 'Финал';
  if (roundsFromFinal === 1 && matchCount === 2 && !isRepechage) {
    return '1/2 финала';
  }
  if (isRepechage) return `Предварительный раунд ${roundNumber}`;
  return `1/${2 ** roundsFromFinal} финала`;
};

const getParticipantNameForSeed = (p?: Participant) =>
  p?.person ? `${p.person.lastName || ''} ${p.person.firstName || ''}`.trim() || 'TBD' : 'TBD';

const buildScoreSuffix = (m: Match, isHome: boolean): string => {
  if (!m.endTime) return '';
  const ippon = isHome ? (m.homeScoreIppon || 0) : (m.awayScoreIppon || 0);
  const wazaAri = isHome ? (m.homeScoreWazaAri || 0) : (m.awayScoreWazaAri || 0);
  const yuko = isHome ? (m.homeScoreYuko || 0) : (m.awayScoreYuko || 0);
  const shido = isHome ? (m.homeShido || 0) : (m.awayShido || 0);
  const parts: string[] = [];
  if (ippon > 0) parts.push(`И:${ippon}`);
  if (wazaAri > 0) parts.push(`В:${wazaAri}`);
  if (yuko > 0) parts.push(`Ю:${yuko}`);
  if (shido > 0) parts.push(`Ш:${shido}`);
  return parts.length > 0 ? ` (${parts.join(' ')})` : '';
};

const mapMatchesToRounds = (matches: Match[] | undefined, isRepechage = false): IRoundProps[] => {
  if (!matches || matches.length === 0) return [];
  const byRound = new Map<number, Match[]>();
  for (const m of matches) {
    const r = m.round || 1;
    if (!byRound.has(r)) byRound.set(r, []);
    byRound.get(r)!.push(m);
  }
  const roundNumbers = Array.from(byRound.keys()).sort((a, b) => a - b);
  return roundNumbers.map((rNum, roundIndex) => {
    const ms = byRound.get(rNum)!.slice().sort((a, b) => (a.order || 0) - (b.order || 0));
    return {
      title: getStageLabel(roundIndex, roundNumbers.length, ms.length, isRepechage, rNum),
      seeds: ms.map((m) => {
        const homeName = getParticipantNameForSeed(m.homeParticipant);
        const awayName = getParticipantNameForSeed(m.awayParticipant);
        return {
          id: m.id ?? `${rNum}-${homeName}-${awayName}`,
          match: m,
          teams: [
            {
              id: m.homeParticipantId ?? undefined,
              name: `${homeName}${buildScoreSuffix(m, true)}`,
              isWinner: m.isHomeWinner === true,
              isLoser: m.isHomeWinner === false,
            },
            {
              id: m.awayParticipantId ?? undefined,
              name: `${awayName}${buildScoreSuffix(m, false)}`,
              isWinner: m.isHomeWinner === false,
              isLoser: m.isHomeWinner === true,
            },
          ],
        } as any;
      }),
    };
  });
};

const BracketView: React.FC = () => {
  const appContext = React.useContext(AppContext);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const bracketClient = new BracketClient(ApiUrl, axios);
  const tournamentClient = new TournamentClient(ApiUrl, axios);

  const [bracket, setBracket] = useState<BracketDetailsModel | undefined>(undefined);
  const [leftRepechageBracket, setLeftRepechageBracket] = useState<BracketDetailsModel | undefined>(undefined);
  const [rightRepechageBracket, setRightRepechageBracket] = useState<BracketDetailsModel | undefined>(undefined);
  const [tournament, setTournament] = useState<Tournament | undefined>(undefined);
  const [winner, setWinner] = useState<WinnerModel | undefined>(undefined);
  const [canCreateLeftRepechage, setCanCreateLeftRepechage] = useState(false);
  const [canCreateRightRepechage, setCanCreateRightRepechage] = useState(false);
  const [loading, setLoading] = useState(true);

  const [mainTab, setMainTab] = useState<MainTab>('bracket');
  const [subTab, setSubTab] = useState<SubTab>('main');
  const playoffAutoSwitchedRef = useRef(false);

  // Repechage preview state
  const [repechagePreview, setRepechagePreview] = useState<BracketPreviewModel | null>(null);
  const [previewSide, setPreviewSide] = useState<RepechageSide | null>(null);
  const [showRepechagePreview, setShowRepechagePreview] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [creatingRepechage, setCreatingRepechage] = useState(false);
  const [printingProtocol, setPrintingProtocol] = useState(false);

  // Match modal state
  const [activeMatchId, setActiveMatchId] = useState<number | null>(null);

  const fetchAll = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const bracketData = await bracketClient.getBracket(+id);
      setBracket(bracketData);

      if (bracketData.tournamentId) {
        try {
          const t = await tournamentClient.getTournament(bracketData.tournamentId);
          setTournament(t);
        } catch {
          // ignore
        }
      }

      try {
        const winnerData = await bracketClient.getBracketWinner(+id);
        setWinner(winnerData);
      } catch {
        setWinner(undefined);
      }

      try {
        const [leftCan, rightCan] = await Promise.all([
          bracketClient.canCreateRepechage(+id, RepechageSide.Left),
          bracketClient.canCreateRepechage(+id, RepechageSide.Right),
        ]);
        setCanCreateLeftRepechage(!!leftCan);
        setCanCreateRightRepechage(!!rightCan);
      } catch {
        setCanCreateLeftRepechage(false);
        setCanCreateRightRepechage(false);
      }

      try {
        const repechageIds = await bracketClient.getRepechageBrackets(+id);
        if (repechageIds.leftRepechageBracketId) {
          const fullDetails = await bracketClient.getBracket(repechageIds.leftRepechageBracketId);
          setLeftRepechageBracket(fullDetails);
        } else {
          setLeftRepechageBracket(undefined);
        }
        if (repechageIds.rightRepechageBracketId) {
          const fullDetails = await bracketClient.getBracket(repechageIds.rightRepechageBracketId);
          setRightRepechageBracket(fullDetails);
        } else {
          setRightRepechageBracket(undefined);
        }
      } catch {
        setLeftRepechageBracket(undefined);
        setRightRepechageBracket(undefined);
      }
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex);
    } finally {
      setLoading(false);
    }
  };

  // Перечитываем только список поединков активной сетки — нужно после закрытия попапа поединка,
  // чтобы не тянуть утешение и победителя заново.
  const refreshActiveBracketMatches = async () => {
    if (!id) return;
    try {
      if (subTab === 'repechage-left' || subTab === 'repechage-right') {
        const currentId = subTab === 'repechage-left'
          ? leftRepechageBracket?.id
          : rightRepechageBracket?.id;
        if (!currentId) return;
        const fresh = await bracketClient.getBracket(currentId);
        if (subTab === 'repechage-left') {
          setLeftRepechageBracket(fresh);
        } else {
          setRightRepechageBracket(fresh);
        }
      } else {
        const fresh = await bracketClient.getBracket(+id);
        setBracket(fresh);
      }

      try {
        const winnerData = await bracketClient.getBracketWinner(+id);
        setWinner(winnerData);
      } catch {
        // победитель ещё не определён — это нормально
      }
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex);
    }
  };

  useEffect(() => {
    fetchAll();
    playoffAutoSwitchedRef.current = false;
  }, [id]);

  useEffect(() => {
    if (subTab === 'repechage-left' && !leftRepechageBracket && !canCreateLeftRepechage) {
      setSubTab('main');
    }
    if (subTab === 'repechage-right' && !rightRepechageBracket && !canCreateRightRepechage) {
      setSubTab('main');
    }
  }, [subTab, leftRepechageBracket, rightRepechageBracket, canCreateLeftRepechage, canCreateRightRepechage]);

  useEffect(() => {
    if (bracket?.format === 'Mixed' && subTab === 'main') {
      setSubTab('group-a');
    }
  }, [bracket?.format, subTab]);

  // Однократный переход на плей-офф после его создания — не блокирует ручной просмотр групп
  useEffect(() => {
    if (
      bracket?.format === 'Mixed'
      && bracket.playoff
      && bracket.groupStageFinished
      && !playoffAutoSwitchedRef.current
    ) {
      setSubTab('playoff');
      playoffAutoSwitchedRef.current = true;
    }
  }, [bracket?.playoff, bracket?.groupStageFinished, bracket?.format]);

  const isRoundRobin = bracket?.format === 'RoundRobin';
  const isMixed = bracket?.format === 'Mixed';
  const isOlympic = bracket?.format === 'Olympic' || (isMixed && subTab === 'playoff');

  const currentBracket = useMemo(() => {
    if (!bracket) return undefined;
    if (isMixed) {
      if (subTab === 'group-a') {
        const group = bracket.groups?.find(g => Number(g.groupIndex) === 1);
        return group ? {
          ...bracket,
          id: group.id,
          matches: group.matches,
          standings: group.standings,
        } : bracket;
      }
      if (subTab === 'group-b') {
        const group = bracket.groups?.find(g => Number(g.groupIndex) === 2);
        return group ? {
          ...bracket,
          id: group.id,
          matches: group.matches,
          standings: group.standings,
        } : bracket;
      }
      if (subTab === 'playoff' && bracket.playoff) {
        return {
          ...bracket,
          id: bracket.playoff.id,
          format: 'Olympic',
          matches: bracket.playoff.matches,
          standings: undefined,
        };
      }
    }
    if (subTab === 'main') return bracket;
    if (subTab === 'repechage-left') return leftRepechageBracket;
    return rightRepechageBracket;
  }, [bracket, subTab, leftRepechageBracket, rightRepechageBracket, isMixed]);

  const isRepechageSubTab = subTab === 'repechage-left' || subTab === 'repechage-right';
  const isGroupSubTab = subTab === 'group-a' || subTab === 'group-b';

  // Для вкладки «Поединки» прячем матчи без обоих участников (TBD vs TBD)
  const matchesByRoundForList = useMemo(() => {
    const groups: Record<number, Match[]> = {};
    if (currentBracket?.matches) {
      for (const m of currentBracket.matches) {
        if (!m.homeParticipantId || !m.awayParticipantId) continue;
        const r = m.round ?? 0;
        if (!groups[r]) groups[r] = [];
        groups[r].push(m);
      }
      for (const r of Object.keys(groups)) {
        groups[Number(r)].sort((a, b) => (a.order || 0) - (b.order || 0));
      }
    }
    return groups;
  }, [currentBracket]);

  const [reordering, setReordering] = useState(false);

  const handleMoveMatchInRound = async (round: number, matchId: number, direction: -1 | 1) => {
    if (!currentBracket?.id || reordering) return;
    const list = matchesByRoundForList[round];
    if (!list) return;
    const idx = list.findIndex(m => m.id === matchId);
    const swapIdx = idx + direction;
    if (idx < 0 || swapIdx < 0 || swapIdx >= list.length) return;

    const nextIds = list.map(m => m.id!);
    [nextIds[idx], nextIds[swapIdx]] = [nextIds[swapIdx], nextIds[idx]];

    setReordering(true);
    try {
      await reorderMatches(currentBracket.id, round, nextIds);
      await refreshActiveBracketMatches();
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex, 'Не удалось изменить порядок поединков');
    } finally {
      setReordering(false);
    }
  };

  const sortedRoundsForList = useMemo(
    () => Object.keys(matchesByRoundForList).map(Number).sort((a, b) => a - b),
    [matchesByRoundForList]
  );

  const handleOpenRepechagePreview = async (side: RepechageSide) => {
    if (!id) return;
    setLoadingPreview(true);
    setPreviewSide(side);
    try {
      const preview = await bracketClient.previewRepechage(+id, side);
      setRepechagePreview(preview);
      setShowRepechagePreview(true);
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex, 'Не удалось загрузить предпросмотр утешения');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleApproveRepechage = async (
    rounds: { roundNumber: number; matches: { homeParticipantId?: number | null; awayParticipantId?: number | null }[] }[]
  ) => {
    if (!id || !previewSide) return;
    try {
      setCreatingRepechage(true);
      const response = await bracketClient.createRepechage(new CreateRepechageRequest({
        mainBracketId: parseInt(id),
        side: previewSide,
        rounds: rounds.map(round => new RoundApproveDto({
          roundNumber: round.roundNumber,
          matches: round.matches.map(match => new MatchApproveDto({
            homeParticipantId: match.homeParticipantId ?? undefined,
            awayParticipantId: match.awayParticipantId ?? undefined,
          })),
        })),
      }));
      const repechageDetails = await bracketClient.getBracket(response.id!);
      if (previewSide === RepechageSide.Left) {
        setLeftRepechageBracket(repechageDetails);
        setCanCreateLeftRepechage(false);
        setSubTab('repechage-left');
      } else {
        setRightRepechageBracket(repechageDetails);
        setCanCreateRightRepechage(false);
        setSubTab('repechage-right');
      }
      setShowRepechagePreview(false);
      setPreviewSide(null);
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex, 'Не удалось создать сетку утешения');
    } finally {
      setCreatingRepechage(false);
    }
  };

  const getParticipantName = (p?: Participant) =>
    p?.person ? `${p.person.lastName || ''} ${p.person.firstName || ''}`.trim() : 'TBD';

  const getStageTitle = (roundMatchesCount: number, roundNumber: number) => {
    const maxRound = sortedRoundsForList.length > 0
      ? sortedRoundsForList[sortedRoundsForList.length - 1]
      : roundNumber;
    const roundIndex = sortedRoundsForList.indexOf(roundNumber);
    const isRepechage = isRepechageSubTab;
    if (isOlympic) {
      return getStageLabel(
        roundIndex >= 0 ? roundIndex : 0,
        sortedRoundsForList.length,
        roundMatchesCount,
        isRepechage,
        roundNumber
      );
    }
    // Круговая и групповой этап смешанной — туры, не раунды олимпийской сетки
    return `Тур ${roundNumber}`;
  };

  const getMatchStatus = (m: Match) => {
    if (m.isHomeWinner === true || m.isHomeWinner === false) return 'Завершён';
    if (m.startTime && !m.endTime) return 'В процессе';
    return 'Не начат';
  };

  const getScoreText = (m: Match, isHome: boolean) => {
    const ippon = isHome ? (m.homeScoreIppon || 0) : (m.awayScoreIppon || 0);
    const wazaAri = isHome ? (m.homeScoreWazaAri || 0) : (m.awayScoreWazaAri || 0);
    const yuko = isHome ? (m.homeScoreYuko || 0) : (m.awayScoreYuko || 0);
    const shido = isHome ? (m.homeShido || 0) : (m.awayShido || 0);

    const parts: string[] = [];
    if (ippon > 0) parts.push(`И:${ippon}`);
    if (wazaAri > 0) parts.push(`В:${wazaAri}`);
    if (yuko > 0) parts.push(`Ю:${yuko}`);
    if (shido > 0) parts.push(`Ш:${shido}`);

    return parts.length > 0 ? ` (${parts.join(' ')})` : '';
  };

  const getDurationText = (m: Match) => {
    if (!m.startTime || !m.endTime) return '';
    const start = new Date(m.startTime).getTime();
    const end = new Date(m.endTime).getTime();
    const paused = m.pausedTime?.totalMilliseconds || 0;
    const ms = Math.max(0, (end - start) - paused);
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleMatchClick = (m: Match) => {
    if (!m.homeParticipantId || !m.awayParticipantId) return;
    setActiveMatchId(m.id!);
  };

  const handleCloseMatchModal = async () => {
    setActiveMatchId(null);
    await fetchAll();
  };

  const semifinalistsReady = useMemo(() => {
    if (!bracket?.matches || bracket.format === 'RoundRobin' || bracket.format === 'Mixed') return false;
    const matches = bracket.matches;
    const semifinals = matches.filter(
      (m) => m.nextMatchId && !matches.some((m2) => m2.id === m.nextMatchId && m2.nextMatchId)
    );
    return semifinals.length === 2
      && semifinals.every((m) => m.homeParticipantId && m.awayParticipantId);
  }, [bracket]);

  const bracketRounds = useMemo<IRoundProps[]>(
    () => mapMatchesToRounds(currentBracket?.matches, isRepechageSubTab),
    [currentBracket, isRepechageSubTab]
  );

  const CustomSeed = useCallback(({ seed, breakpoint }: IRenderSeedProps) => {
    const m: Match | undefined = (seed as any).match;
    const teams = (seed as any).teams as Array<{ id?: number; name?: string; isWinner?: boolean; isLoser?: boolean }>;
    const isClickable = !!(m && m.homeParticipantId && m.awayParticipantId);
    const isFinished = m?.isHomeWinner === true || m?.isHomeWinner === false;
    const inProgress = !!m?.startTime && !m?.endTime;

    const teamStyle = (t: { id?: number; isWinner?: boolean; isLoser?: boolean }): React.CSSProperties => {
      if (t.isWinner) {
        return { background: '#16a34a', color: '#ffffff', borderRadius: 6, padding: '6px 10px', fontWeight: 600 };
      }
      if (t.isLoser) {
        return { background: '#fee2e2', color: '#991b1b', borderRadius: 6, padding: '6px 10px', textDecoration: 'line-through' };
      }
      if (!t.id) {
        return { background: '#f1f5f9', color: '#94a3b8', borderRadius: 6, padding: '6px 10px', fontStyle: 'italic' };
      }
      return { background: '#1d4ed8', color: '#ffffff', borderRadius: 6, padding: '6px 10px' };
    };

    const wrapperStyle: React.CSSProperties = {
      cursor: isClickable ? 'pointer' : 'default',
      padding: 4,
      borderRadius: 8,
      border: inProgress ? '2px solid #f59e0b' : '2px solid transparent',
      background: inProgress ? '#fffbeb' : 'transparent',
      transition: 'all 0.15s ease',
    };

    return (
      <Seed mobileBreakpoint={breakpoint}>
        <SeedItem>
          <div
            style={wrapperStyle}
            onClick={() => m && isClickable && handleMatchClick(m)}
            onMouseEnter={(e) => {
              if (isClickable && !inProgress) {
                (e.currentTarget as HTMLDivElement).style.borderColor = '#3b82f6';
              }
            }}
            onMouseLeave={(e) => {
              if (!inProgress) {
                (e.currentTarget as HTMLDivElement).style.borderColor = 'transparent';
              }
            }}
          >
            <SeedTeam style={teamStyle(teams[0])}>
              {teams[0]?.name || 'TBD'}
            </SeedTeam>
            <SeedTeam style={{ ...teamStyle(teams[1]), marginTop: 6 }}>
              {teams[1]?.name || 'TBD'}
            </SeedTeam>
            {inProgress && (
              <div style={{
                marginTop: 6,
                textAlign: 'center',
                fontSize: 11,
                fontWeight: 700,
                color: '#b45309',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}>
                {m?.isPaused ? 'Пауза' : 'Идёт поединок'}
              </div>
            )}
            {isFinished && (
              <div style={{
                marginTop: 4,
                textAlign: 'center',
                fontSize: 10,
                color: '#6b7280',
              }}>
                Завершён
              </div>
            )}
          </div>
        </SeedItem>
      </Seed>
    );
  }, []);

  if (loading) {
    return <div className="bracket-view__loading">Загрузка данных сетки...</div>;
  }

  if (!bracket) {
    return <div className="bracket-view__loading">Сетка не найдена</div>;
  }

  const handlePrintProtocol = async () => {
    if (!id) return;
    setPrintingProtocol(true);
    try {
      if (isMixed) {
        await bracketPrintClient.printMixedProtocol(+id);
      } else {
        await bracketPrintClient.printOlympicProtocol(+id);
      }
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex, 'Не удалось сформировать PDF протокола');
    } finally {
      setPrintingProtocol(false);
    }
  };

  const canPrintOlympicProtocol =
    bracket?.format === 'Olympic'
    || isMixed;

  const showRepechageSubtabs = bracket?.format === 'Olympic' && (
    !!leftRepechageBracket ||
    !!rightRepechageBracket ||
    canCreateLeftRepechage ||
    canCreateRightRepechage
  );

  const previewSideLabel = previewSide === RepechageSide.Left
    ? 'левая половина'
    : previewSide === RepechageSide.Right
      ? 'правая половина'
      : '';

  return (
    <div className="bracket-view">
      <div className="bracket-view__header">
        <div className="bracket-view__header-top">
          <button onClick={() => navigate(`/tournaments/${bracket.tournamentId}`)} className="btn btn--back">
            ← К турниру
          </button>
          {isAdmin() && (
            <button onClick={() => navigate(`/tournaments/${bracket.tournamentId}/edit`)} className="btn btn--edit">
              Редактировать турнир
            </button>
          )}
        </div>

        <h2>{bracket.tournamentName}</h2>
        <div className="bracket-view__info">
          <span><strong>Возрастная категория:</strong> {bracket.ageCategory}</span>
          <span><strong>Весовая категория:</strong> {bracket.weightCategory}</span>
          <span><strong>Формат:</strong> {isMixed ? 'Смешанная' : isRoundRobin ? 'Круговая' : 'Олимпийская'}</span>
        </div>

        {canPrintOlympicProtocol && (
          <div className="bracket-view__print-bar">
            <button
              type="button"
              className="bracket-view__print-btn"
              onClick={handlePrintProtocol}
              disabled={printingProtocol}
            >
              {printingProtocol ? 'Формирование PDF…' : 'Печать протокола хода соревнований'}
            </button>
          </div>
        )}

        {winner && (subTab === 'main' || subTab === 'playoff') && (
          <div className="bracket-view__winner-banner">
            <h3>Победитель</h3>
            <p>{winner.personName} ({winner.organization})</p>
          </div>
        )}
      </div>

      {/* Главные табы */}
      <div className="bracket-view__tabs">
        <button
          className={`bracket-view__tab ${mainTab === 'bracket' ? 'bracket-view__tab--active' : ''}`}
          onClick={() => setMainTab('bracket')}
        >
          {isMixed ? 'Таблица / Сетка' : isRoundRobin ? 'Таблица' : 'Сетка'}
        </button>
        <button
          className={`bracket-view__tab ${mainTab === 'matches' ? 'bracket-view__tab--active' : ''}`}
          onClick={() => setMainTab('matches')}
        >
          Поединки
        </button>
      </div>

      {/* Сабтабы для смешанного формата */}
      {isMixed && (
        <div className="bracket-view__subtabs">
          <button
            className={`bracket-view__subtab ${subTab === 'group-a' ? 'bracket-view__subtab--active' : ''}`}
            onClick={() => setSubTab('group-a')}
          >
            Группа A
          </button>
          <button
            className={`bracket-view__subtab ${subTab === 'group-b' ? 'bracket-view__subtab--active' : ''}`}
            onClick={() => setSubTab('group-b')}
          >
            Группа B
          </button>
          {bracket.playoff && (
            <button
              className={`bracket-view__subtab ${subTab === 'playoff' ? 'bracket-view__subtab--active' : ''}`}
              onClick={() => setSubTab('playoff')}
            >
              Плей-офф
            </button>
          )}
        </div>
      )}

      {/* Сабтабы для утешения */}
      {showRepechageSubtabs && (
        <div className="bracket-view__subtabs">
          <button
            className={`bracket-view__subtab ${subTab === 'main' ? 'bracket-view__subtab--active' : ''}`}
            onClick={() => setSubTab('main')}
          >
            Основная
          </button>
          {(leftRepechageBracket || canCreateLeftRepechage) && (
            <button
              className={`bracket-view__subtab ${subTab === 'repechage-left' ? 'bracket-view__subtab--active' : ''}`}
              onClick={() => setSubTab('repechage-left')}
            >
              Утешение (левая)
            </button>
          )}
          {(rightRepechageBracket || canCreateRightRepechage) && (
            <button
              className={`bracket-view__subtab ${subTab === 'repechage-right' ? 'bracket-view__subtab--active' : ''}`}
              onClick={() => setSubTab('repechage-right')}
            >
              Утешение (правая)
            </button>
          )}
        </div>
      )}

      {isOlympic && semifinalistsReady && !showRepechageSubtabs && subTab === 'main' && (
        <div className="bracket-view__repechage-hint">
          {isAdmin() ? (
            <p>
              Утешение для этой категории пока недоступно: нет проигравших в четвертьфиналах
              (обычно нужно <strong>минимум 8 участников</strong> в олимпийской сетке).
            </p>
          ) : (
            <p>
              Сетки утешения для этой категории не сформированы или недоступны
              (требуются четвертьфиналы и достаточное число участников).
            </p>
          )}
        </div>
      )}

      {!isAdmin() && subTab === 'main' && isOlympic && (canCreateLeftRepechage || canCreateRightRepechage) && (
        <div className="bracket-view__repechage-hint">
          <p>
            Сетки утешения готовы к формированию. Попросите администратора турнира открыть
            эту сетку и нажать «Предпросмотр утешения».
          </p>
        </div>
      )}

      {isAdmin() && subTab === 'main' && isOlympic && (canCreateLeftRepechage || canCreateRightRepechage) && (
        <div className="bracket-view__create-repechage">
          <p>
            Четвертьфиналы доиграны и 4 полуфиналиста известны. Сетки утешения создаются
            отдельно для левой и правой половины основной сетки. Проигравшие полуфиналисты
            противоположной половины подтянутся в финал за бронзу автоматически.
          </p>
          <div className="bracket-view__create-repechage-actions">
            {canCreateLeftRepechage && (
              <button
                onClick={() => handleOpenRepechagePreview(RepechageSide.Left)}
                disabled={loadingPreview}
                className="btn btn--register"
              >
                {loadingPreview && previewSide === RepechageSide.Left ? 'Загрузка...' : 'Предпросмотр утешения (левая)'}
              </button>
            )}
            {canCreateRightRepechage && (
              <button
                onClick={() => handleOpenRepechagePreview(RepechageSide.Right)}
                disabled={loadingPreview}
                className="btn btn--register"
              >
                {loadingPreview && previewSide === RepechageSide.Right ? 'Загрузка...' : 'Предпросмотр утешения (правая)'}
              </button>
            )}
          </div>
        </div>
      )}

      {isRepechageSubTab && !currentBracket && isAdmin() && (
        <div className="bracket-view__create-repechage">
          <p>Сетка утешения для этой половины ещё не создана.</p>
          {subTab === 'repechage-left' && canCreateLeftRepechage && (
            <button
              onClick={() => handleOpenRepechagePreview(RepechageSide.Left)}
              disabled={loadingPreview}
              className="btn btn--register"
            >
              Предпросмотр утешения (левая)
            </button>
          )}
          {subTab === 'repechage-right' && canCreateRightRepechage && (
            <button
              onClick={() => handleOpenRepechagePreview(RepechageSide.Right)}
              disabled={loadingPreview}
              className="btn btn--register"
            >
              Предпросмотр утешения (правая)
            </button>
          )}
        </div>
      )}

      {/* Контент: Сетка */}
      {mainTab === 'bracket' && (
        <>
          {isRoundRobin || isGroupSubTab ? (
            <div className="bracket-view__standings">
              <h3>Таблица очков</h3>
              {(currentBracket?.standings && currentBracket.standings.length > 0) ? (
                <table className="bracket-view__standings-table">
                  <thead>
                    <tr>
                      <th>Место</th>
                      <th>Участник</th>
                      <th>Организация</th>
                      <th>Победы</th>
                      <th>Поражения</th>
                      <th>Очки</th>
                      <th>Иппон</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentBracket.standings.map((s) => (
                      <tr key={s.participantId}>
                        <td>{s.place ?? '—'}</td>
                        <td>{s.personName}</td>
                        <td>{s.organization || '—'}</td>
                        <td>{s.wins}</td>
                        <td>{s.losses}</td>
                        <td><strong>{s.points}</strong></td>
                        <td>{s.ippons}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="bracket-view__empty">Поединки ещё не проведены.</p>
              )}
            </div>
          ) : bracketRounds.length > 0 ? (
            <div className="bracket-view__diagram">
              <Bracket
                rounds={bracketRounds}
                mobileBreakpoint={0}
                roundTitleComponent={(title) => (
                  <div className="bracket-view__round-title">{title}</div>
                )}
                renderSeedComponent={CustomSeed}
              />
            </div>
          ) : (
            <p className="bracket-view__empty">Матчи не найдены в этой сетке</p>
          )}
        </>
      )}

      {/* Контент: Поединки */}
      {mainTab === 'matches' && (
        <>
          {sortedRoundsForList.length === 0 ? (
            <div className="bracket-view__empty">Поединков пока нет (ждут определения участников).</div>
          ) : (
            <div className="bracket-view__matches-list">
              {isAdmin() && (
                <p className="bracket-view__reorder-hint">
                  Стрелки меняют очерёдность внутри тура/раунда. Состав пар и номер тура не меняются.
                </p>
              )}
              {sortedRoundsForList.map(round => (
                <div key={round} className="bracket-view__matches-group">
                  <h3>{getStageTitle(matchesByRoundForList[round].length, round)}</h3>
                  <ul>
                    {matchesByRoundForList[round].map((m, matchIndex) => {
                      const isFinished = m.isHomeWinner === true || m.isHomeWinner === false;
                      const inProgress = !!(m.startTime && !m.endTime);
                      const homeStyle = isFinished
                        ? { color: m.isHomeWinner ? '#16a34a' : '#dc2626', fontWeight: 700 }
                        : undefined;
                      const awayStyle = isFinished
                        ? { color: !m.isHomeWinner ? '#16a34a' : '#dc2626', fontWeight: 700 }
                        : undefined;
                      const canMoveUp = isAdmin() && matchIndex > 0;
                      const canMoveDown = isAdmin() && matchIndex < matchesByRoundForList[round].length - 1;
                      return (
                        <li
                          key={m.id}
                          className={`bracket-view__match-row ${isFinished ? 'is-finished' : ''} ${inProgress ? 'is-in-progress' : ''}`}
                          onClick={() => handleMatchClick(m)}
                        >
                          <span className="bracket-view__match-row-main">
                            {isAdmin() && (
                              <span
                                className="bracket-view__reorder"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  type="button"
                                  className="bracket-view__reorder-btn"
                                  title="Выше"
                                  disabled={!canMoveUp || reordering}
                                  onClick={() => m.id && handleMoveMatchInRound(round, m.id, -1)}
                                >
                                  ↑
                                </button>
                                <button
                                  type="button"
                                  className="bracket-view__reorder-btn"
                                  title="Ниже"
                                  disabled={!canMoveDown || reordering}
                                  onClick={() => m.id && handleMoveMatchInRound(round, m.id, 1)}
                                >
                                  ↓
                                </button>
                              </span>
                            )}
                            <span>
                              <span style={homeStyle}>
                                {getParticipantName(m.homeParticipant)}
                                {m.endTime && <span className="bracket-view__match-row-score">{getScoreText(m, true)}</span>}
                              </span>
                              <span className="bracket-view__match-row-vs"> против </span>
                              <span style={awayStyle}>
                                {getParticipantName(m.awayParticipant)}
                                {m.endTime && <span className="bracket-view__match-row-score">{getScoreText(m, false)}</span>}
                              </span>
                              {m.endTime && (
                                <span className="bracket-view__match-row-meta">
                                  • {getDurationText(m)}
                                </span>
                              )}
                            </span>
                          </span>
                          <span className={`bracket-view__match-row-status ${isFinished ? 'is-finished' : ''} ${inProgress ? 'is-in-progress' : ''}`}>
                            {getMatchStatus(m)}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Модалка превью утешения */}
      {showRepechagePreview && repechagePreview && (
        <div
          className="bracket-view__preview-overlay"
          onClick={() => !creatingRepechage && setShowRepechagePreview(false)}
        >
          <div className="bracket-view__preview-modal" onClick={e => e.stopPropagation()}>
            <div className="bracket-view__preview-header">
              <div>
                <h3>Предпросмотр сетки утешения ({previewSideLabel})</h3>
                <p>{repechagePreview.weightCategory} · {repechagePreview.ageCategory}</p>
              </div>
              <button
                className="btn btn--back"
                onClick={() => setShowRepechagePreview(false)}
                disabled={creatingRepechage}
              >
                ✕ Закрыть
              </button>
            </div>
            <BracketsPreview
              data={repechagePreview}
              onApprove={creatingRepechage ? undefined : handleApproveRepechage}
            />
            {creatingRepechage && (
              <p className="bracket-view__preview-loading">Создание сетки утешения...</p>
            )}
          </div>
        </div>
      )}

      {/* Модалка проведения поединка */}
      <MatchConductModal
        isOpen={activeMatchId !== null}
        matchId={activeMatchId}
        onClose={handleCloseMatchModal}
      />
    </div>
  );
};

export default BracketView;
